import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './App.css';

function Products() {
  const navigate = useNavigate();
  const [pageContent, setPageContent] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesList, setCategoriesList] = useState([]);

  const scrollToSection = (id) => {
    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch page content
        const { data: content, error: contentError } = await supabase
          .from('page_content')
          .select('*')
          .eq('id', 1)
          .single();

        if (contentError && contentError.code !== 'PGRST116') {
          console.error('Error fetching page content:', contentError);
        }

        // Fetch products
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .order('created_at', { ascending: false });

        if (productsError) {
          console.error('Error fetching products:', productsError);
        }

        setPageContent(content);
        setProducts(productsData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('name')
          .order('name', { ascending: true });

        if (error) {
          console.error('Error fetching categories:', error);
          // Fallback to default categories if fetch fails
          setCategoriesList(['BORMAK Kaynak Makinesi Setleri', 'BORMAK Tekli Kaynak Makineleri', 'ERAL Boru Makasları', 'Plastik Boru Kalemtıraşları', 'ERAL Test Pompaları', 'Plastik Boru İşleme & Montaj Aparatları', 'Rezistanslar']);
        } else {
          setCategoriesList(data?.map(c => c.name) || ['BORMAK Kaynak Makinesi Setleri', 'BORMAK Tekli Kaynak Makineleri', 'ERAL Boru Makasları', 'Plastik Boru Kalemtıraşları', 'ERAL Test Pompaları', 'Plastik Boru İşleme & Montaj Aparatları', 'Rezistanslar']);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategoriesList(['BORMAK Kaynak Makinesi Setleri', 'BORMAK Tekli Kaynak Makineleri', 'ERAL Boru Makasları', 'Plastik Boru Kalemtıraşları', 'ERAL Test Pompaları', 'Plastik Boru İşleme & Montaj Aparatları', 'Rezistanslar']);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Yükleniyor...
      </div>
    );
  }

  // Default content if not loaded from database
  const content = pageContent || {
    products_title: 'Ürün Kataloğumuz',
    products_subtitle: 'Geniş ürün yelpazemizle ihtiyacınıza uygun çözümler',
    logo_url: '',
    whatsapp_phone: '',
    contact_phone: ''
  };

  // Group products by category
  const groupedProducts = categoriesList.map(cat => ({
    category: cat,
    products: products.filter(p => p.category === cat)
  }));

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            {content.logo_url ? (
              <img src={content.logo_url} alt="BORMAK" className="logo-image" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
            ) : (
              <span className="logo-text" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>BORMAK</span>
            )}
          </div>
          <ul className="nav-menu">
            <li><button onClick={() => navigate('/')}>Ana Sayfa</button></li>
            <li><button onClick={() => navigate('/products')} style={{ color: '#1e3a8a', fontWeight: 'bold' }}>Ürünler</button></li>
            <li><button onClick={() => navigate('/#about')}>Hakkımızda</button></li>
            <li><button onClick={() => navigate('/#contact')}>İletişim</button></li>
          </ul>
        </div>
      </nav>

      {/* Products Section */}
      <section id="products" className="products" style={{ paddingTop: '6rem' }}>
        <div className="container">
          <h2 className="section-title">{content.products_title}</h2>
          <p className="section-subtitle">{content.products_subtitle}</p>
          
          <div className="product-categories">
            {groupedProducts.map(({ category, products: categoryProducts }) => (
              categoryProducts.length > 0 && (
                <div key={category} className="category">
                  <h3 className="category-title">{category}</h3>
                  <div className="product-grid">
                    {categoryProducts.map(product => (
                      <div key={product.id} className="product-card">
                        {product.image_url ? (
                          <div className="product-image-container">
                            <img src={product.image_url} alt={product.name} className="product-image" />
                          </div>
                        ) : (
                          <div className="product-icon">{product.icon || '⚡'}</div>
                        )}
                        <h4>{product.name}</h4>
                        <p>{product.description}</p>
                        {product.price ? (
                          <div style={{ marginTop: '1rem' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '0.5rem' }}>
                              {(() => {
                                const trimmedPrice = product.price.trim();
                                const upperPrice = trimmedPrice.toUpperCase();
                                return upperPrice.endsWith('TL') ? trimmedPrice : `${trimmedPrice} TL`;
                              })()}
                            </div>
                          </div>
                        ) : (
                          (() => {
                            const whatsappNumber = content.whatsapp_phone || content.contact_phone?.replace(/\D/g, '') || '';
                            const productName = encodeURIComponent(product.name);
                            const message = encodeURIComponent(`Merhaba, ${product.name} hakkında bilgi almak istiyorum.`);
                            return whatsappNumber ? (
                              <a
                                href={`https://wa.me/${whatsappNumber}?text=${message}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-small"
                                style={{ 
                                  backgroundColor: '#25D366', 
                                  color: 'white',
                                  textDecoration: 'none',
                                  display: 'inline-block',
                                  marginTop: '1rem',
                                  width: '100%'
                                }}
                              >
                                📱 WhatsApp ile İletişim
                              </a>
                            ) : (
                              <button 
                                className="btn btn-small"
                                onClick={() => navigate('/#contact')}
                                style={{ 
                                  marginTop: '1rem',
                                  width: '100%'
                                }}
                              >
                                Fiyat Bilgisi Alınız
                              </button>
                            );
                          })()
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
            {products.length === 0 && (
              <div className="category">
                <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                  Henüz ürün eklenmemiş.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>BORMAK</h3>
              <p>Plastik Boru Kaynak Makinası</p>
            </div>
            <div className="footer-links">
              <button onClick={() => navigate('/')}>Ana Sayfa</button>
              <button onClick={() => navigate('/products')}>Ürünler</button>
              <button onClick={() => navigate('/#about')}>Hakkımızda</button>
              <button onClick={() => navigate('/#contact')}>İletişim</button>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{content.footer_text || '© 2024 AET BORMAK - Altinel Plastik. Tüm hakları saklıdır.'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Products;

